import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    ActionViewSearchRequest,
    ActionViewUrgency,
    TaskActionViewCompletedOption,
    TaskActionViewDateTimeOption,
    TaskActionViewThatAreOption,
    TaskActionViewUrgencyOption,
} from '../actionView.models';

@Component({
    selector: 'action-view-date-criteria',
    templateUrl: './actionViewDateCriteria.component.html',
    standalone: false,
})
export class ActionViewDateCriteriaComponent {
    @Input({ required: true }) criteria: ActionViewSearchRequest;
    @Input() urgencies: ActionViewUrgency[] = [];
    @Output() criteriaChange = new EventEmitter<ActionViewSearchRequest>();

    readonly dateTime = TaskActionViewDateTimeOption;
    readonly completed = TaskActionViewCompletedOption;
    readonly thatAre = TaskActionViewThatAreOption;

    private readonly parsedDates = new Map<string, Date | undefined>();

    setDateTimeChoice(dateTimeChoice: TaskActionViewDateTimeOption): void {
        this.update({
            dateTimeChoice,
            ...(dateTimeChoice === TaskActionViewDateTimeOption.DueWithinDays && !this.criteria.dueWithinDays
                ? { dueWithinDays: 120 }
                : {}),
        });
    }

    setNumber(
        property: 'dueWithinDays' | 'lastActivityFrom' | 'lastActivityTo' | 'completedWithinDays',
        value: number | string | null,
    ): void {
        const numberValue = value === '' || value === null ? null : Number(value);
        this.update({ [property]: Number.isFinite(numberValue) ? numberValue : null });
    }

    setBoolean(
        property:
            | 'includeUnscheduled'
            | 'onlyUnscheduled'
            | 'useUrgencyFilter'
            | 'useLastActivityFilter'
            | 'includeSkipped'
            | 'onlySkipped',
        value: boolean,
    ): void {
        const changes: Partial<ActionViewSearchRequest> = { [property]: value };
        if (property === 'includeUnscheduled' && !value) {
            changes.onlyUnscheduled = false;
        }
        if (property === 'includeSkipped' && !value) {
            changes.onlySkipped = false;
        }
        if (property === 'useUrgencyFilter') {
            changes.urgencyChoice = value ? TaskActionViewUrgencyOption.Overdue : null;
        }
        this.update(changes);
    }

    setUrgency(value: TaskActionViewUrgencyOption): void {
        this.update({ urgencyChoice: Number(value) as TaskActionViewUrgencyOption });
    }

    setCompletedChoice(value: TaskActionViewCompletedOption): void {
        this.update({ completedChoice: Number(value) as TaskActionViewCompletedOption });
    }

    setDate(property: 'dueDateFrom' | 'dueDateTo' | 'completedDateFrom' | 'completedDateTo', value: Date): void {
        this.update({ [property]: value });
    }

    validateLastActivity(property: 'lastActivityFrom' | 'lastActivityTo'): void {
        const { lastActivityFrom, lastActivityTo } = this.criteria;
        if (lastActivityFrom === null || lastActivityTo === null || lastActivityFrom <= lastActivityTo) {
            return;
        }
        this.update({ [property]: null });
    }

    asDate(value: Date | string | null): Date | undefined {
        if (!value) {
            return undefined;
        }
        if (value instanceof Date) {
            return value;
        }
        // Return the same Date instance for the same string so the date picker input doesn't see a new value on
        // every change detection pass and reset what the user just picked.
        if (!this.parsedDates.has(value)) {
            const date = new Date(value);
            this.parsedDates.set(value, Number.isNaN(date.getTime()) ? undefined : date);
        }
        return this.parsedDates.get(value);
    }

    private update(changes: Partial<ActionViewSearchRequest>): void {
        this.criteriaChange.emit({ ...this.criteria, ...changes });
    }
}
